import axios, { AxiosInstance, AxiosResponse, AxiosError, InternalAxiosRequestConfig } from 'axios';
import toast from 'react-hot-toast';
import { API_BASE_URL } from '@/config/env';
import { ErrorCode, getErrorInfo, extractErrorFromResponse, canRefreshToken, requiresLogin } from '../utils/errorCodes';

// API 에러 구조 정의
export interface ApiError {
  response?: {
    status: number;
    data?: {
      error?: string;
      details?: Array<{ path: string; msg: string }>;
      code?: string;
      message?: string;
    };
  };
  request?: any;
  message: string;
  config: any;
  code?: string;
  name?: string;
}

// Create axios instance
export const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Redux store reference
let storeDispatch: any = null;

export const setStoreDispatch = (dispatch: any): void => {
  storeDispatch = dispatch;
};

// Token refresh state
let isRefreshing = false;
let refreshPromise: Promise<{ accessToken: string; user: any; userType: string }> | null = null;

// Token refresh function
export const refreshTokenFn = async (): Promise<{ accessToken: string; user: any; userType: string }> => {
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }

  isRefreshing = true;

  refreshPromise = (async () => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/auth/refresh`,
        {},
        {
          withCredentials: true,
          headers: { 'X-Skip-Interceptor': 'true' }
        }
      );

      // 백엔드는 Jackson SNAKE_CASE 직렬화 — 응답 필드는 access_token/user_type/admin_role.
      // (initializeAuth/brandLogin/verifyVerificationCode 의 토큰 파싱과 동일 키 사용.)
      const { access_token, user, user_type, admin_role } = response.data.data;
      // admin 은 role 을 user 에 주입해 권한 그라데이션(manager vs admin) 일관 유지.
      const nextUser = (user_type === 'admin' && admin_role && user) ? { ...user, role: admin_role } : user;
      setAuthToken(access_token);
      // refresh 성공 시 공유 세션 플래그 재설정 — 일시적 401 이 지운 플래그 복구(새 탭 로그아웃 캐스케이드 방지).
      void import('../store/slices/authSlice').then((m) => m.setSessionFlag()).catch(() => {});

      // 갱신 시 accessToken 만이 아니라 user/userType 까지 atomic 복원 (userType=null race 방지).
      // user_type/user 누락 응답이면 복원을 스킵해 기존 인증 상태 보존 — userType=undefined 로 덮어써
      // ProtectedRoute 가드가 영구 로딩에 빠지는 deadlock 방지. (axios 헤더는 위에서 이미 갱신됨.)
      if (storeDispatch && user_type && nextUser) {
        const { setAuthenticated } = await import('../store/slices/authSlice');
        storeDispatch(setAuthenticated({ accessToken: access_token, user: nextUser, userType: user_type }));
      }

      return { accessToken: access_token, user: nextUser, userType: user_type };
    } catch (error: any) {
      clearAuth();

      if (storeDispatch) {
        const { clearAuthState, clearSessionFlag } = await import('../store/slices/authSlice');
        clearSessionFlag();
        storeDispatch(clearAuthState());
      }

      // ProtectedRoute가 isAuthenticated=false 감지 시 /login으로 Navigate 처리
      // window.location.href 사용 시 풀 페이지 리로드로 채팅 모달 등의 상태가 소멸됨
      throw error;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

// Request interceptor
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig<any>) => config,
  (error: AxiosError) => Promise.reject(error)
);

// Response interceptor - ErrorCode 기반 에러 핸들링
api.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: ApiError) => {
    const originalRequest = error.config;

    // 요청 취소/네트워크 에러 처리
    if (error.message === 'Request aborted' || error.code === 'ERR_CANCELED' || error.name === 'AbortError') {
      return Promise.reject(error);
    }
    if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') {
      toast.error('Network error. Please check your connection.');
      return Promise.reject(error);
    }

    // 특정 요청에 대해 인터셉터 스킵
    if (originalRequest && (originalRequest as any).headers?.['X-Skip-Interceptor']) {
      return Promise.reject(error);
    }
    if (originalRequest && (
      originalRequest.url?.includes('/auth/refresh') ||
      originalRequest.url?.includes('/auth/send-verification-code') ||
      originalRequest.url?.includes('/auth/verify-code') ||
      originalRequest.url?.includes('/auth/register-brand')
    )) {
      return Promise.reject(error);
    }

    // 에러 응답에서 에러 정보 추출
    if (error.response) {
      // ApiResponse.error 구조: { error: { code?, message } } 또는 레거시 { error: "string" }
      const errorField = error.response.data?.error as any;
      const errorCode = (typeof errorField === 'object' && errorField?.code)
        ? errorField.code
        : (typeof errorField === 'string' ? errorField : error.response.data?.code || '');
      const errorInfo = extractErrorFromResponse(error);

      // 401 에러 시 토큰 갱신 시도 (모든 401 응답에 대해 리프레시 시도)
      if (error.response.status === 401 ||
          canRefreshToken(errorCode) ||
          errorCode === 'ACCESS_TOKEN_EXPIRED' ||
          (typeof errorField === 'string' && errorField === 'Access token expired')) {

        // 이미 재시도한 요청이 또 401 → 실제 세션 만료로 판단
        if ((originalRequest as any)._retry) {
          clearAuth();
          if (storeDispatch) {
            const { clearAuthState } = await import('../store/slices/authSlice');
            storeDispatch(clearAuthState());
          }
          toast.error(errorInfo.userMessage);
          return Promise.reject(error);
        }

        // 동시 401 은 큐잉 — 첫 요청이 refresh 를 구동하고 나머지는 in-flight refreshPromise 를 공유(refreshTokenFn 가드).
        // 이전엔 isRefreshing 이면 즉시 로그아웃 경로로 빠져 오탐 "Session expired" 토스트/순간 미인증이 발생했음.
        (originalRequest as any)._retry = true;
        try {
          const refreshResult = await refreshTokenFn();
          // 갱신된 토큰으로 원래 요청의 Authorization 헤더 업데이트 후 재시도
          if (originalRequest.headers) {
            originalRequest.headers['Authorization'] = `Bearer ${refreshResult.accessToken}`;
          }
          return api(originalRequest);
        } catch (refreshError) {
          // refresh 가 실제로 실패했을 때만 auth 클리어 + 토스트
          clearAuth();
          if (storeDispatch) {
            const { clearAuthState } = await import('../store/slices/authSlice');
            storeDispatch(clearAuthState());
          }
          toast.error('Session expired. Please sign in again.');
          return Promise.reject(refreshError);
        }
      }

      // 특정 에러 코드로 로그인이 필요한 경우
      if (requiresLogin(errorCode)) {
        clearAuth();
        if (storeDispatch) {
          const { clearAuthState } = await import('../store/slices/authSlice');
          storeDispatch(clearAuthState());
        }
        toast.error(errorInfo.userMessage);
        return Promise.reject(error);
      }

      // HTTP 상태 코드별 기본 처리
      // 400/422: 비즈니스 에러 → 호출측에서 컨텍스트에 맞는 메시지 표시 (인터셉터는 토스트 안 띄움)
      // 403/429/500: 시스템 에러 → 인터셉터에서 토스트 표시
      switch (error.response.status) {
        case 400:
        case 422:
          // 비즈니스 에러 - 호출측 onError 핸들러에서 처리하도록 위임
          break;
        case 403:
          toast.error(errorInfo.userMessage || 'Access denied. Insufficient permissions.');
          (error as any)._interceptorHandled = true;
          break;
        case 404:
          // 404는 호출측에서 처리하는 경우가 많으므로 위임
          break;
        case 429:
          toast.error('Too many requests. Please wait a moment and try again.');
          (error as any)._interceptorHandled = true;
          break;
        case 500:
          toast.error('Something went wrong. Please try again later.');
          (error as any)._interceptorHandled = true;
          break;
        default:
          // 기타 에러 코드 - 호출측에서 처리하도록 위임
          break;
      }
    } else if (error.request) {
      toast.error('Network error. Please check your connection.');
    } else {
      toast.error('An unexpected error occurred. Please try again.');
    }

    return Promise.reject(error);
  }
);

// Auth token helpers
export const setAuthToken = (token: string | null): void => {
  if (token) {
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    delete api.defaults.headers.common['Authorization'];
  }
};

export const clearAuth = (): void => {
  delete api.defaults.headers.common['Authorization'];
};

export default api;
